import CloseIcon from "@mui/icons-material/Close";
import SearchIcon from "@mui/icons-material/Search";
import { IconButton, InputAdornment, ListItem, ListItemText, TextField } from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { StyledAutoComplete } from "./StyledComponents";
import { useTranslation } from "react-i18next";

const CustomAutocomplete = (props) => {
  const language = useSelector((state) => state.languageR.language);
  const {
    multiple = false,
    id,
    freeSolo = false,
    options = [],
    placeholder,
    required = false,
    onDataChange = () => { },
    name,
    dataType,
    disabledValues = false,
    buttonColor,
    className,
    isError = false,
    setIsError = () => { },
    resetTrigger,
    initialValue,
    value,
    clear,
  } = props;

  const getLabel = (option) => {
    if (!option || typeof option === "string") return option || "";

    switch (dataType) {
      case "user":
        return option.fullName || "";
      case "userGroup":
        return option.groupCode || "";
      case "shift":
        return option.shiftName || "";
      case "grouporuser":
        return option.groupName || "";
      case "userOrGroup":
        return option.groupName || option.fullName || "";
      case "group":
        if (option.groupName?.trim().toLowerCase() === "all users") {
          return t("common.all-user");
        }
        return option.groupName || "";
      default:
        return "";
    }
  };

  const getValue = (option) => {
    if (!option) return "";
    switch (dataType) {
      case "user":
        return option.userId;
      case "userGroup":
        return option.groupCode;
      case "shift":
        return option.shiftId;
      case "grouporuser":
        return option.id;
      case "userOrGroup":
        return option.groupId ? option.groupId : option.userId;
      default:
        return "";
    }
  };

  const [inputValue, setInputValue] = useState("");
  const [selectedStatus, setSelectedStatus] = useState(false);
  const { t } = useTranslation();

  useEffect(() => {
    if (initialValue) {
      setInputValue(getLabel(initialValue));
      setSelectedStatus(true);
    }
  }, [initialValue]);

  const handleClear = () => {
    setInputValue("");
    setSelectedStatus(false);
    onDataChange(name, "");
    setIsError(false);
  };

  useEffect(() => {
    if (dataType === "grouporuser" || (dataType === "user" && clear)) {
      handleClear();
    }
  }, [dataType, clear]);

  const handleOnChange = (event, newValue) => {
    if (newValue) {
      setSelectedStatus(true);
      onDataChange(name, newValue);
      setIsError(false);
    } else {
      handleClear();
    }
  };

  const highlightMatch = (text, query) => {
    const regex = new RegExp(`(${query})`, "gi");
    return text.replace(
      regex,
      '<span style="background-color: yellow;">$1</span>'
    );
  };

  useEffect(() => {
    handleClear();
  }, [resetTrigger]);

  useEffect(() => {
    if (value) {
      setInputValue(getLabel(value));
      setSelectedStatus(true);
    }
  }, [value]);

  const normalizeVietnamese = (str = "") => {
    return str
      .normalize("NFD") // tách dấu
      .replace(/[\u0300-\u036f]/g, "") // xoá dấu
      .replace(/đ/g, "d")
      .replace(/Đ/g, "D")
      .toLowerCase();
  };

  const defaultBorderColor = isError ? "#EF4444" : "#E5E7EB";

  const inputOutlineSx = {
    "& fieldset": {
      border: `2px solid ${defaultBorderColor}`,
      borderRadius: "10px",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${defaultBorderColor}`,
      borderRadius: "10px",
    },
    // Giữ border mặc định, không đổi khi hover/focus
    "& .MuiInputBase-root:hover fieldset, & .MuiOutlinedInput-root:hover fieldset": {
      border: `2px solid ${defaultBorderColor} !important`,
    },
    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: `${defaultBorderColor} !important`,
    },
    "& .MuiInputBase-root.Mui-focused fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": {
      border: `2px solid ${defaultBorderColor} !important`,
      boxShadow: "none !important",
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: `2px solid ${defaultBorderColor} !important`,
    },
    "& .MuiOutlinedInput-root.Mui-focused": {
      boxShadow: "none",
    },
  };

  return (
    <StyledAutoComplete
      sx={{
        height: "40px",

        "& .MuiFormControl-root": {
          height: "40px",
          border: "none",
          backgroundColor: "transparent",
          padding: 0,
        },

        "& .MuiInputBase-root": {
          minHeight: "40px !important",
          height: "40px !important",
          borderRadius: "10px",
          backgroundColor: "#fff",
          padding: "0 !important",
          border: "none",
        },

        "& .MuiOutlinedInput-root": {
          height: "40px !important",
          minHeight: "40px !important",
          padding: "0 6px 0 4px !important",
          display: "flex",
          alignItems: "center",
          borderRadius: "10px",
          backgroundColor: "#fff",
          color: "#1F2937",
          overflow: "visible",
        },

        ...inputOutlineSx,

        "& .MuiOutlinedInput-input": {
          height: "40px !important",
          padding: "0 8px !important",
          paddingRight: "36px !important",
          boxSizing: "border-box",
          color: "#1F2937",
          "&::placeholder": {
            color: "#9CA3AF",
            opacity: 1,
          },
        },

        "& .MuiAutocomplete-endAdornment": {
          height: "40px",
          display: "flex",
          alignItems: "center",
          right: "8px !important",
          top: "50%",
          transform: "translateY(-50%)",
        },

        "& .MuiInputBase-root .MuiButtonBase-root.MuiIconButton-root": {
          backgroundColor: "transparent !important",
          color: "#1F2937",
          borderRadius: "10px !important",
          width: "28px !important",
          height: "28px !important",
          minWidth: "28px !important",
          m: 0,
          p: "4px !important",
          boxShadow: "none",
          border: "none",
          "&.Mui-disabled": {
            opacity: 0.4,
          },
          "& .MuiSvgIcon-root": {
            fontSize: 16,
            color: "#1F2937",
          },
        },
      }}
      className={className}
      multiple={multiple}
      id={id}
      disabled={disabledValues}
      freeSolo={freeSolo}
      options={options}
      ListboxProps={{
        sx: {
          maxHeight: 200,
          padding: 0,
          "& .MuiAutocomplete-option": {
            minHeight: 42,
            paddingTop: "10px",
            paddingBottom: "10px",
            color: "#1F2937",
            "&:hover, &.Mui-focused": {
              backgroundColor: "transparent",
            },
            '&[aria-selected="true"], &[aria-selected="true"].Mui-focused': {
              backgroundColor: "transparent",
            },
          },
        },
      }}
      componentsProps={{
        paper: {
          sx: {
            marginTop: "4px",
            borderRadius: "8px",
            boxShadow:
              "0px 4px 6px -1px rgba(0, 0, 0, 0.1), 0px 2px 4px -2px rgba(0, 0, 0, 0.1)",
          },
        },
      }}
      getOptionLabel={getLabel}
      noOptionsText="No options available"
      isOptionEqualToValue={(option, value) =>
        getValue(option) === getValue(value)
      }
      value={value}
      renderOption={(props, option) => {
        if (option.custom) {
          return (
            <ListItem {...props} key="no-data" className="pointer-events-none">
              <ListItemText primary={option.label} />
            </ListItem>
          );
        }

        const label = highlightMatch(getLabel(option), inputValue);
        return (
          <ListItem {...props} key={getValue(option)}>
            <ListItemText
              primary={<div dangerouslySetInnerHTML={{ __html: label }} />}
            />
          </ListItem>
        );
      }}
      filterOptions={(options, state) => {
        const filteredOptions = options?.filter((option) =>
          normalizeVietnamese(getLabel(option)).includes(
            normalizeVietnamese(state.inputValue)
          )
        );
        if (filteredOptions?.length === 0) {
          return [{ custom: true, label: t("common.content-160") }];
        }

        return filteredOptions;
      }}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setIsError(true);
        setInputValue(newInputValue);
        if (newInputValue === "") {
          // When input is cleared, ensure the error is reset
          setIsError(false);
        }
      }}
      // onBlur={handleBlur}
      readOnly={selectedStatus}
      onChange={handleOnChange}
      disableClearable={true}
      renderInput={(params) => (
        <TextField
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault(); // ❌ chặn Enter
            }
          }}
          required={required}
          {...params}
          placeholder={placeholder}
          sx={{
            "&.MuiTextField-root": {
              border: "none",
              backgroundColor: "transparent",
              padding: 0,
            },
            "& .MuiOutlinedInput-root": {
              height: "40px",
              minHeight: "40px",
              padding: "0 6px 0 4px",
              display: "flex",
              alignItems: "center",
              borderRadius: "10px",
              backgroundColor: "#fff",
              overflow: "visible",
            },
            ...inputOutlineSx,
            "& .MuiOutlinedInput-input": {
              height: "40px",
              padding: "0 8px",
              paddingRight: "36px",
              boxSizing: "border-box",
              color: "#1F2937",
              "&::placeholder": {
                color: "#9CA3AF",
                opacity: 1,
              },
            },
            "& .MuiAutocomplete-endAdornment": {
              height: "40px",
              m: 0,
              right: "8px !important",
            },
          }}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <InputAdornment position="start" sx={{ mr: 0, ml: 1 }}>
                  <SearchIcon fontSize="small" sx={{ color: "#D0D5DD" }} />
                </InputAdornment>
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: !multiple && (
              <>
                {params.InputProps.endAdornment}
                <IconButton
                  onClick={handleClear}
                  aria-label="Clear"
                  edge="end"
                  disabled={!inputValue}
                  sx={{
                    borderRadius: "10px",
                    width: 18,
                    height: 18,
                    p: 0.5,
                  }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </>
            ),
          }}
        />
      )}

    />
  );
};

export default CustomAutocomplete;
